<template>
  <!-- The backend pre-renders this same markup into #app from the same
       content file (render_guide_html in backend/app.py), so crawlers get the
       full article without running JS. Keep the two in sync. -->
  <div v-if="guide" class="guide-page">
    <content-header :path="pagePath" :languages="languages" />

    <main class="guide-main">
      <article class="guide-article">
        <h1>{{ page.title }}</h1>
        <p class="guide-meta">{{ dates }}</p>
        <p class="guide-lead">{{ page.lead }}</p>
        <div class="guide-body" v-html="page.body.join('\n')"></div>
        <p v-if="guide.startCta" class="guide-start">
          <a class="guide-start-link" :href="controllerPath">{{ labels.start }} →</a>
        </p>
        <aside :class="['guide-booking', { 'guide-booking-highlight': guide.bookingHighlight }]">
          <h2>{{ labels.bookingTitle }}</h2>
          <p>{{ labels.bookingText }}</p>
          <a class="guide-booking-cta" :href="labels.bookingUrl" target="_blank" rel="noopener">{{ labels.bookingCta }} →</a>
        </aside>
        <div v-if="page.note" class="guide-note" v-html="page.note"></div>
      </article>

      <nav class="guide-related" aria-labelledby="guide-related-title">
        <h2 id="guide-related-title">{{ labels.moreGuides }}</h2>
        <ul>
          <li v-for="other in otherGuides" :key="other.slug">
            <router-link :to="guidePath(other.slug)">{{ other[$locale].title }}</router-link>
            <p>{{ other[$locale].summary }}</p>
          </li>
        </ul>
        <router-link class="guide-all" :to="`${prefix}/guides`">{{ labels.allGuides }} →</router-link>
      </nav>
    </main>

    <content-footer />
  </div>
  <!-- Unknown slug: the backend already answered with a 404 -->
  <not-found-view v-else />
</template>

<script>
import content from '../public/content/guides.json';
import { applySeo } from './i18n/head';
import NotFoundView from './NotFoundView.vue';
import ContentHeader from './components/ContentHeader.vue';
import { LOCALES, localePrefix } from './i18n/locales';
import ContentFooter from './components/ContentFooter.vue';
import { guideDates } from './contentPages';

export default {
  components: { NotFoundView, ContentHeader, ContentFooter },
  computed: {
    // Not found when the guide isn't translated into this language
    guide() {
      return content.guides.find(g => g.slug === this.$route.params.slug && g[this.$locale]) || null;
    },
    // Languages this guide is translated into
    languages() {
      return LOCALES.filter(code => this.guide && this.guide[code]);
    },
    page() {
      return this.guide[this.$locale];
    },
    labels() {
      return content.labels[this.$locale];
    },
    prefix() {
      return localePrefix(this.$locale);
    },
    dates() {
      return guideDates(this.guide, this.$locale);
    },
    pagePath() {
      return `/guides/${this.$route.params.slug}`;
    },
    controllerPath() {
      return `${this.prefix}/controller`;
    },
    // The next few guides after this one (wrapping around), so every guide
    // links to a different set. Same selection as render_guide_html.
    otherGuides() {
      const all = content.guides.filter(g => g[this.$locale]);
      const start = all.indexOf(this.guide);
      return [1, 2, 3, 4].map(i => all[(start + i) % all.length]).filter(g => g !== this.guide);
    }
  },
  watch: {
    '$route'() {
      this.updateSeo();
      window.scrollTo(0, 0);
    }
  },
  created() {
    this.updateSeo();
  },
  methods: {
    guidePath(slug) {
      return `${this.prefix}/guides/${slug}`;
    },
    updateSeo() {
      if (!this.guide) return;
      applySeo({
        locale: this.$locale,
        routeBase: `guides/${this.guide.slug}`,
        title: this.page.metaTitle,
        description: this.page.metaDescription,
        languages: this.languages
      });
    }
  }
};
</script>

<!-- Deliberately not scoped: the server-rendered copy of this page has no
     data-v attributes, and it must look the same before the app mounts -->
<style>
.guide-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--c-bg);
}
.guide-header {
  padding: 1.1rem clamp(1rem, 5vw, 4rem);
  background: #16302c;
}
.guide-header-inner {
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.guide-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: white;
  font-family: var(--ff-heading);
  font-weight: 600;
  text-decoration: none;
}
.guide-brand:hover {
  color: white;
}
.guide-brand img {
  border-radius: 8px;
  object-fit: cover;
}
.guide-nav {
  display: flex;
  align-items: center;
  gap: 1.2rem;
}
.guide-nav > a {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
}
.guide-nav > a:hover {
  color: var(--c-accent-light);
}
.guide-main {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(2.5rem, 7vh, 4rem) 16px 4rem;
}
.guide-article h1 {
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  color: var(--c-text);
  margin-bottom: 1rem;
}
.guide-meta {
  color: var(--c-text-light);
  font-size: 0.85rem;
  margin-bottom: 1rem;
}
.guide-lead {
  color: var(--c-text);
  font-size: 1.08rem;
  line-height: 1.7;
  margin-bottom: 0.5rem;
}
.guide-body h2 {
  font-size: 1.4rem;
  color: var(--c-text);
  margin: 2.2rem 0 0.8rem;
}
.guide-body p,
.guide-body li {
  color: var(--c-text-light);
  font-size: 1rem;
  line-height: 1.75;
}
.guide-body p + p,
.guide-body p + ul,
.guide-body p + ol,
.guide-body ul + p,
.guide-body ol + p {
  margin-top: 0.9rem;
}
.guide-body ul,
.guide-body ol {
  padding-left: 1.3rem;
}
.guide-body li + li {
  margin-top: 0.5rem;
}
.guide-body strong {
  color: var(--c-text);
  font-weight: 600;
}
.guide-body a,
.guide-note a {
  color: var(--c-primary);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.guide-table {
  overflow-x: auto;
  margin-top: 0.5rem;
}
.guide-table table {
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 0.92rem;
}
.guide-table th,
.guide-table td {
  padding: 0.7rem 0.8rem;
  border-bottom: 1px solid var(--c-border);
  text-align: left;
  vertical-align: top;
  line-height: 1.5;
}
.guide-table thead th {
  color: var(--c-text);
  font-weight: 600;
  border-bottom: 2px solid var(--c-border);
}
.guide-table tbody th {
  color: var(--c-text);
  font-weight: 600;
  white-space: nowrap;
}
.guide-table td {
  color: var(--c-text-light);
}
.guide-start {
  margin-top: 2.2rem;
}
.guide-start-link {
  display: inline-block;
  padding: 0.8rem 1.6rem;
  border-radius: 10px;
  background: var(--c-primary);
  color: white;
  font-weight: 600;
}
button.guide-start-link {
  border: none;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}
.guide-start-link:hover {
  background: var(--c-primary-light);
  color: white;
}
.guide-booking {
  margin-top: 2.5rem;
  padding: 1.5rem 1.6rem;
  border: 1px solid var(--c-border);
  border-left: 3px solid var(--c-accent);
  border-radius: 12px;
  background: white;
}
.guide-booking-highlight {
  background: #16302c;
  border-color: #16302c;
  border-left-color: var(--c-accent);
}
.guide-booking h2 {
  font-size: 1.25rem;
  color: var(--c-text);
  margin-bottom: 0.5rem;
}
.guide-booking p {
  color: var(--c-text-light);
  font-size: 0.96rem;
  line-height: 1.65;
  margin-bottom: 1.1rem;
}
.guide-booking-highlight h2 {
  color: white;
}
.guide-booking-highlight p {
  color: rgba(255, 255, 255, 0.78);
}
.guide-booking-cta {
  display: inline-block;
  padding: 0.75rem 1.4rem;
  border-radius: 10px;
  background: var(--c-accent);
  color: #1a2f2c;
  font-weight: 600;
}
.guide-booking-cta:hover {
  background: var(--c-accent-light);
  color: #1a2f2c;
}
.guide-note {
  margin-top: 1.5rem;
  padding: 1rem 1.2rem;
  border-radius: 10px;
  background: var(--c-bg-alt);
}
.guide-note p {
  color: var(--c-text);
  font-size: 0.92rem;
  line-height: 1.6;
}
.guide-related {
  margin-top: 3.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--c-border);
}
.guide-related h2 {
  font-size: 1.15rem;
  color: var(--c-text);
  margin-bottom: 0.9rem;
}
.guide-related ul {
  list-style: none;
}
.guide-related li + li {
  margin-top: 0.9rem;
}
.guide-related a {
  font-weight: 600;
}
.guide-related p {
  color: var(--c-text-light);
  font-size: 0.9rem;
  line-height: 1.5;
}
.guide-all {
  display: inline-block;
  margin-top: 1.2rem;
  color: var(--c-primary);
  font-weight: 600;
}
.guide-hub-title {
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  color: var(--c-text);
  margin-bottom: 1rem;
}
.guide-hub-list {
  list-style: none;
  margin-top: 2rem;
}
.guide-hub-list li {
  padding: 1.4rem 0;
  border-top: 1px solid var(--c-border);
}
.guide-hub-list li:last-child {
  border-bottom: 1px solid var(--c-border);
}
.guide-hub-list h2 {
  font-size: 1.3rem;
  margin-bottom: 0.4rem;
}
.guide-hub-list h2 a {
  color: var(--c-text);
}
.guide-hub-list h2 a:hover {
  color: var(--c-primary);
}
.guide-hub-list p {
  color: var(--c-text-light);
  line-height: 1.65;
}
.guide-hub-list .guide-meta {
  margin: 0.4rem 0 0;
}
@media (max-width: 560px) {
  .guide-brand span {
    display: none;
  }
  .guide-nav {
    gap: 0.9rem;
  }
  .guide-nav > a {
    font-size: 0.85rem;
  }
}
.guide-footer {
  margin-top: auto;
  padding: 1.5rem 16px;
  background: #141F1D;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.82rem;
  text-align: center;
}
.guide-footer a {
  color: var(--c-accent-light);
}
.guide-footer .footer-note {
  max-width: 640px;
  margin: 0.6rem auto 0;
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.45);
}
</style>
